/**
 * Cylinder Camera Utilities
 *
 * Provides camera position calculations for viewing cylinders from
 * various orientations relative to the cylinder's local axis.
 *
 * @module cylinderCameraUtils
 */

const THREE = window.THREE

/**
 * Calculate camera position for viewing a cylinder from a specific direction
 * relative to its axis.
 *
 * @param {Object} cylinderData - Cylinder measurement data
 * @param {Object} cylinderData.position - Center position {x, y, z}
 * @param {Object} cylinderData.axis - Axis vector {x, y, z} (from bottom to top)
 * @param {number} cylinderData.radius - Cylinder radius
 * @param {number} cylinderData.height - Cylinder height
 * @param {string} direction - View direction: 'front', 'back', 'left', 'right', 'top', 'bottom'
 * @param {number} [distanceMultiplier=3] - Multiplier for camera distance
 * @returns {Object} { position: Vector3, lookAt: Vector3, up: Vector3 }
 */
export function calculateAxisBasedCameraPosition(cylinderData, direction, distanceMultiplier = 3) {
  if (!cylinderData) {
    return null
  }

  if (!cylinderData.position || !cylinderData.axis) {
    return null
  }

  // Convert cylinder data to Three.js vectors
  const center = new THREE.Vector3(
    cylinderData.position.x,
    cylinderData.position.y,
    cylinderData.position.z
  )

  const axisVector = new THREE.Vector3(
    cylinderData.axis.x,
    cylinderData.axis.y,
    cylinderData.axis.z
  )

  // Normalize axis
  const axis = normalizeVector3(axisVector)

  // Calculate perpendicular vectors
  const { perp1, perp2 } = getOrthogonalBasis(axis)

  // Calculate camera distance
  const distance = calculateCameraDistance(
    cylinderData.radius,
    cylinderData.height,
    distanceMultiplier
  )

  let cameraPosition
  let cameraUp = new THREE.Vector3(0, 0, 1) // Default up vector

  switch (direction) {
    case 'front':
    case 'axis-front':
      // View along positive axis direction
      cameraPosition = center.clone().add(axis.clone().multiplyScalar(distance))
      cameraUp = perp1.clone() // Use perpendicular as up
      break

    case 'back':
    case 'axis-back':
      // View along negative axis direction
      cameraPosition = center.clone().add(axis.clone().multiplyScalar(-distance))
      cameraUp = perp1.clone()
      break

    case 'left':
    case 'axis-left':
      // View from left perpendicular
      cameraPosition = center.clone().add(perp1.clone().multiplyScalar(distance))
      cameraUp = axis.clone() // Use axis as up for side views
      break

    case 'right':
    case 'axis-right':
      // View from right perpendicular
      cameraPosition = center.clone().add(perp1.clone().multiplyScalar(-distance))
      cameraUp = axis.clone()
      break

    case 'top':
    case 'axis-top':
      // View from top perpendicular (orthogonal to left/right)
      cameraPosition = center.clone().add(perp2.clone().multiplyScalar(distance))
      cameraUp = axis.clone()
      break

    case 'bottom':
    case 'axis-bottom':
      // View from bottom perpendicular (orthogonal to left/right)
      cameraPosition = center.clone().add(perp2.clone().multiplyScalar(-distance))
      cameraUp = axis.clone()
      break

    default:
      cameraPosition = center.clone().add(axis.clone().multiplyScalar(distance))
      cameraUp = perp1.clone()
  }

  return {
    position: cameraPosition,
    lookAt: center,
    up: cameraUp
  }
}

/**
 * Normalize a 3D vector to unit length
 *
 * @param {Object|Vector3} vector - Vector to normalize {x, y, z} or THREE.Vector3
 * @returns {Vector3} Normalized THREE.Vector3
 */
export function normalizeVector3(vector) {
  if (!vector) {
    return new THREE.Vector3(0, 0, 1) // Default to Z-up
  }

  const v = vector instanceof THREE.Vector3
    ? vector.clone()
    : new THREE.Vector3(vector.x, vector.y, vector.z)

  const length = v.length()

  if (length === 0) {
    return new THREE.Vector3(0, 0, 1)
  }

  return v.normalize()
}

/**
 * Calculate two orthogonal (perpendicular) vectors to a given axis vector
 * using Gram-Schmidt orthogonalization process.
 *
 * Handles the singularity case when axis is parallel to the world up vector.
 *
 * @param {Vector3} axis - Normalized axis vector
 * @returns {Object} { perp1: Vector3, perp2: Vector3 }
 */
export function getOrthogonalBasis(axis) {
  // Ensure axis is normalized
  const normalizedAxis = axis.clone().normalize()

  // Choose an arbitrary reference vector
  // We'll use world X-axis (1, 0, 0) by default
  let reference = new THREE.Vector3(1, 0, 0)

  // Check if axis is too parallel to X-axis (singularity case)
  // If dot product is close to 1 or -1, vectors are parallel
  const dotX = Math.abs(normalizedAxis.dot(reference))

  if (dotX > 0.9) {
    // Axis is too close to X-axis, use Y-axis instead
    reference = new THREE.Vector3(0, 1, 0)

    // Check if parallel to Y-axis
    const dotY = Math.abs(normalizedAxis.dot(reference))

    if (dotY > 0.9) {
      // Use Z-axis as last resort
      reference = new THREE.Vector3(0, 0, 1)
    }
  }

  // Calculate first perpendicular vector using cross product
  const perp1 = new THREE.Vector3()
    .crossVectors(normalizedAxis, reference)
    .normalize()

  // Calculate second perpendicular vector
  // It's perpendicular to both axis and perp1
  const perp2 = new THREE.Vector3()
    .crossVectors(normalizedAxis, perp1)
    .normalize()

  return { perp1, perp2 }
}

/**
 * Calculate appropriate camera distance based on cylinder dimensions
 *
 * @param {number} radius - Cylinder radius
 * @param {number} height - Cylinder height
 * @param {number} [multiplier=3] - Distance multiplier
 * @returns {number} Camera distance
 */
export function calculateCameraDistance(radius, height, multiplier = 3) {
  if (!radius || !height || radius <= 0 || height <= 0) {
    return 10 // Default fallback distance
  }

  // Use the larger dimension to ensure entire cylinder is visible
  const maxDimension = Math.max(radius * 2, height)

  // Apply multiplier for comfortable viewing distance
  return maxDimension * multiplier
}

/**
 * Calculate angle between two vectors in degrees
 *
 * @param {Vector3} v1 - First vector
 * @param {Vector3} v2 - Second vector
 * @returns {number} Angle in degrees
 */
export function angleBetweenVectors(v1, v2) {
  if (!v1 || !v2) return 0

  const dot = v1.dot(v2)
  const magnitudes = v1.length() * v2.length()

  if (magnitudes === 0) return 0

  const cosAngle = THREE.MathUtils.clamp(dot / magnitudes, -1, 1)
  return THREE.MathUtils.radToDeg(Math.acos(cosAngle))
}

/**
 * Project a point onto a line defined by a point and direction
 *
 * @param {Vector3} point - Point to project
 * @param {Vector3} linePoint - Point on the line
 * @param {Vector3} lineDirection - Normalized direction of line
 * @returns {Vector3} Projected point on the line
 */
export function projectPointOnLine(point, linePoint, lineDirection) {
  if (!point || !linePoint || !lineDirection) {
    return new THREE.Vector3()
  }

  const pointToLine = new THREE.Vector3().subVectors(point, linePoint)
  const distance = pointToLine.dot(lineDirection)

  return new THREE.Vector3()
    .copy(lineDirection)
    .multiplyScalar(distance)
    .add(linePoint)
}

/**
 * Extract cylinder axis data from cylinder measurement
 * Handles both array and object formats for position and axis
 *
 * @param {Object} cylinderMeasurement - Cylinder measurement from activeMeasurements
 * @returns {Object} { center: Vector3, axis: Vector3, radius: number, height: number }
 */
export function extractCylinderData(cylinderMeasurement) {
  if (!cylinderMeasurement || cylinderMeasurement.type !== 'fitted-cylinder') {
    return null
  }

  const data = cylinderMeasurement.cylinderData || cylinderMeasurement.data

  if (!data) {
    return null
  }

  // Handle different position formats
  let center
  if (data.position) {
    center = Array.isArray(data.position)
      ? new THREE.Vector3(...data.position)
      : new THREE.Vector3(data.position.x, data.position.y, data.position.z)
  } else {
    return null
  }

  // Handle different axis formats
  let axis
  if (data.axis) {
    axis = Array.isArray(data.axis)
      ? new THREE.Vector3(...data.axis)
      : new THREE.Vector3(data.axis.x, data.axis.y, data.axis.z)
  } else {
    return null
  }

  return {
    position: center,
    axis: axis,
    radius: data.radius || 1,
    height: data.height || 1
  }
}

/**
 * Validate cylinder data structure
 *
 * @param {Object} cylinderData - Cylinder data to validate
 * @returns {boolean} True if valid
 */
export function validateCylinderData(cylinderData) {
  if (!cylinderData) return false

  const hasPosition = cylinderData.position &&
    typeof cylinderData.position.x === 'number' &&
    typeof cylinderData.position.y === 'number' &&
    typeof cylinderData.position.z === 'number'

  const hasAxis = cylinderData.axis &&
    typeof cylinderData.axis.x === 'number' &&
    typeof cylinderData.axis.y === 'number' &&
    typeof cylinderData.axis.z === 'number'

  const hasRadius = typeof cylinderData.radius === 'number' && cylinderData.radius > 0
  const hasHeight = typeof cylinderData.height === 'number' && cylinderData.height > 0

  return hasPosition && hasAxis && hasRadius && hasHeight
}

// Export default object for convenience
export default {
  calculateAxisBasedCameraPosition,
  normalizeVector3,
  getOrthogonalBasis,
  calculateCameraDistance,
  angleBetweenVectors,
  projectPointOnLine,
  extractCylinderData,
  validateCylinderData
}
